import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ContentType } from 'types/publishedAssets';
import { PageContext } from 'src/context/PageContext';
import { FeedItem } from 'types/feeds';
import { ImageBoss } from 'components/Content/Atoms/ImageBoss';
import { ActionButton, ButtonType } from 'components/Atoms/ActionButton/ActionButton';
import { Icon } from 'components/Atoms/Icon/Icon';
import { HeroIllustration } from 'components/Content/ContentTypes/Hero/HeroIllustration';
import { useIsRTL } from 'utils/useIsRTL';
import { buildPublicPathFromSlug } from 'utils/publicUrlRule';
import styles from './FeaturedProject.module.scss';

interface FeaturedProjectMetadata {
  project_uuids?: string[];
}

interface FeaturedProjectProps {
  content: ContentType;
}

export function FeaturedProject(props: FeaturedProjectProps) {
  const { content } = props;
  const { t } = useTranslation();
  const { feeds, urlParams, linkHostname } = useContext(PageContext);
  const { isRTL } = useIsRTL();
  const [currentIndex, setCurrentIndex] = useState(0);

  const metadata = content.metadata as FeaturedProjectMetadata;

  // Get project feed data - projects are stored in feeds with content.id as key
  const feedData = useMemo<FeedItem[]>(() => {
    if (!feeds || !content.id) return [];
    return (feeds[content.id] || []) as FeedItem[];
  }, [feeds, content.id]);

  // Filter feed data to only include projects with matching UUIDs
  const projects = useMemo<FeedItem[]>(() => {
    const projectUuids = metadata?.project_uuids || [];
    if (projectUuids.length === 0) return [];
    return feedData.filter((item) => projectUuids.includes(item.id));
  }, [feedData, metadata]);

  const hasMultipleProjects = projects.length > 1;
  const currentProject = projects[currentIndex];

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? projects.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === projects.length - 1 ? 0 : prev + 1));
  };

  if (!currentProject) {
    return null;
  }

  const { organization, locale } = urlParams || {};
  const projectClickUrl = buildPublicPathFromSlug(
    [`${organization}`, `${locale}`, 'pages', currentProject.slug || currentProject.id],
    linkHostname,
  );

  const {
    title: mainTitle,
    description: mainDescription,
    image: mainImage,
    imageAltText: mainImageAltText,
    buttonLabel: mainButtonLabel,
    fallbackTitle,
    fallbackDescription,
    fallbackImage,
    fallbackImageAltText,
    fallbackButtonLabel,
  } = currentProject;
  const title = mainTitle || fallbackTitle;
  const description = mainDescription || fallbackDescription;
  const image = mainImage || fallbackImage;
  const imageAltText = mainImageAltText || fallbackImageAltText;
  const buttonLabel = mainButtonLabel || fallbackButtonLabel;

  return (
    <div className={styles.featuredProjectContainer}>
      <div className={styles.imageContainer}>
        {image && <ImageBoss url={image} alt={imageAltText || title || ''} className={styles.image} width={1200} />}
        {!image && <HeroIllustration variant="dark" className={styles.imageIllustration} isRTL={isRTL} />}
        {hasMultipleProjects && (
          <div className={styles.navButtonsContainer}>
            <button className={styles.navButton} onClick={handlePrevious} aria-label="Previous project" type="button">
              <Icon name="chevron-left" />
            </button>
            <button className={styles.navButton} onClick={handleNext} aria-label="Next project" type="button">
              <Icon name="chevron-right" />
            </button>
          </div>
        )}
      </div>

      <div className={styles.contentOverlay}>
        <div className={styles.textBlock}>
          <h2 className={styles.title}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
          <div className={styles.buttonContainer}>
            <ActionButton type={ButtonType.ACTION} href={projectClickUrl}>
              {buttonLabel || t('feed.learnMore')}
            </ActionButton>
          </div>
        </div>
      </div>

      {hasMultipleProjects && (
        <div className={styles.progressContainer}>
          <div className={styles.progressBar}>
            <div
              className={styles.progressFill}
              style={{ width: `${((currentIndex + 1) / projects.length) * 100}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
